import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { ChecklistStatus, checkContractRequest } from 'stores/ContractRequestStoreAdapter';
import { formatDateTime } from 'utils/DateTimeFormatter';
import { errorDefault } from 'utils/Toastify';

type ChecklistSectionProps = {
  contractRequestId: string;
  checklists: ChecklistStatus[];
  onChecklistUpdate: (updated: ChecklistStatus) => void;
  /** 管理部門かどうか（チェック操作可否） */
  canOperate: boolean;
  /** 承認済みかどうか（Wチェックのみ操作可能） */
  isApproved: boolean;
};

const ChecklistSection: React.FC<ChecklistSectionProps> = ({
  contractRequestId,
  checklists,
  onChecklistUpdate,
  canOperate,
  isApproved,
}) => {
  const sortedChecklists = [...checklists].sort((a, b) => a.sortOrder - b.sortOrder);

  const handleCheck = (item: ChecklistStatus, field: 'isChecked' | 'isWChecked') => {
    if (!canOperate) return;
    if (isApproved && field === 'isChecked') return;

    const newValue = !item[field];
    const params = {
      contractRequestId,
      checklistId: item.checklistId,
      isChecked: field === 'isChecked' ? newValue : item.isChecked,
      isWChecked: field === 'isWChecked' ? newValue : item.isWChecked,
    };

    checkContractRequest(params)
      .then(() => {
        onChecklistUpdate({
          ...item,
          [field]: newValue,
        });
      })
      .catch((e: Error) => errorDefault('チェックの更新に失敗しました', e.message));
  };

  if (sortedChecklists.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
        チェックリスト項目がありません
      </Typography>
    );
  }

  // 進捗: チェック + Wチェック の両方が完了した項目を「完了」とみなす
  const completed = sortedChecklists.filter((i) => i.isChecked && i.isWChecked).length;
  const total = sortedChecklists.length;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  const allDone = completed === total;

  return (
    <Stack spacing={2}>
      {/* 進捗サマリー */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            チェック進捗
          </Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: allDone ? 'success.main' : 'text.primary' }}
          >
            {completed} / {total} 完了
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={percent}
          color={allDone ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>

      {sortedChecklists.map((item) => {
        const itemDone = item.isChecked && item.isWChecked;
        return (
          <Box
            key={item.checklistId}
            sx={{
              border: 1,
              borderColor: itemDone ? 'success.main' : 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              transition: 'border-color 0.2s',
            }}
          >
            {/* 項目名 + 状態アイコン */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
            >
              {itemDone ? (
                <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
                />
              )}
              <Typography variant="body2" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
                {item.label}
              </Typography>
            </Stack>

            {/* チェック行 */}
            <CheckRow
              label="チェック"
              checked={item.isChecked}
              operatorName={item.checkedByName}
              checkedAt={item.checkedAt}
              disabled={!canOperate || isApproved}
              onChange={() => handleCheck(item, 'isChecked')}
            />

            {/* Wチェック行 */}
            <CheckRow
              label="Wチェック"
              checked={item.isWChecked}
              operatorName={item.wCheckedByName}
              checkedAt={item.wCheckedAt}
              disabled={!canOperate}
              onChange={() => handleCheck(item, 'isWChecked')}
            />
          </Box>
        );
      })}
    </Stack>
  );
};

// --- CheckRow ---
type CheckRowProps = {
  label: string;
  checked: boolean;
  operatorName: string | null;
  checkedAt: string | null;
  disabled: boolean;
  onChange: () => void;
};

const CheckRow: React.FC<CheckRowProps> = ({
  label,
  checked,
  operatorName,
  checkedAt,
  disabled,
  onChange,
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{ px: 1.5, py: 0.5, '&:not(:last-child)': { borderBottom: 1, borderColor: 'divider' } }}
  >
    <Checkbox size="small" checked={checked} onChange={onChange} disabled={disabled} />
    <Typography
      variant="caption"
      fontWeight={600}
      sx={{ minWidth: 64, color: checked ? 'text.primary' : 'text.secondary' }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1 }} />
    <Typography variant="caption" color="text.secondary" noWrap>
      {operatorName || '—'}
    </Typography>
    <Typography variant="caption" color="text.disabled" noWrap>
      {checkedAt ? formatDateTime(checkedAt) : '—'}
    </Typography>
  </Stack>
);

export default ChecklistSection;
